import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from './siteNav';
import { useSubscription } from '../../contexts/SubscriptionContext';

// How long each slide holds before the hero advances on its own.
const SLIDE_INTERVAL_MS = 6000;

// Where "Download" goes. /welcome serves the installer, but it opens with a
// sign-in for anyone signed out — the wrong first step for a new visitor, who
// gets the plans page instead.
const DOWNLOAD_PAGE = '/welcome';

/* ------------------------------------------------------------------ *
 * Slide content
 * ------------------------------------------------------------------ */

// The FBI figures on the "Bracing for impact" slide, one to a line.
const FBI_NUMBERS = ['1,008,587 complaints', '$26,699 Average losses', '$20.8 Billion stolen.'];

/*
 * Each slide (10-1 mockup) is a three-step headline, a short piece of copy and
 * one photograph. `position` is the crop anchor for the desktop panel;
 * `compactPosition` the one for the narrow portrait panel below lg, which shows
 * about a third of the photo's width and so needs the subject named.
 */
const SLIDES = [
  {
    id: 'paradigm-shift',
    labelLines: ['Paradigm', 'shift'],
    headline: ['Innovation', 'Dynamic', 'Proactive'],
    image: {
      src: '/images/site/hero/paradigm-shift-brain.jpeg',
      alt: 'A brain erupting in a burst of colour',
      position: 'object-right',
      // The brain's stem sits ~70% across a 3.3:1 frame. In the phone-size
      // portrait crop (120x150 / 140x170, about a quarter of the width) 78%
      // puts the stem on the crop's centre line with the whole brain and its
      // splash in frame; at 70% the right side of the brain was cut off.
      compactPosition: 'object-[78%_50%]',
    },
    body: (
      <>
        A Neurodivergent Human-centric AI solution inspired by Sun Tzu Art of War
        that&rsquo;s designed for, focused on and targeting online scammers.
      </>
    ),
  },
  {
    id: 'bracing-for-impact',
    labelLines: ['Bracing for', 'impact'],
    headline: ['Stalked', 'Abused', 'Trashed'],
    image: {
      src: '/images/site/hero/bracing-for-impact.jpeg',
      alt: 'An older man resting his chin on his hand, deep in thought',
      position: 'object-right',
      // The man's face sits about two-thirds across the photo.
      compactPosition: 'object-[68%_50%]',
    },
    body: (
      <>
        <span className="block">2025 FBI / IC3 Internet Crime Numbers</span>
        {FBI_NUMBERS.map((line) => (
          <span key={line} className="block">
            <span aria-hidden="true">&rarr;</span> {line}
          </span>
        ))}
      </>
    ),
  },
  {
    id: 'not-happening-to-me',
    labelLines: ['Can’t happen', 'to me'],
    headline: ['Unaware', 'Targeted', 'Victim'],
    image: {
      src: '/images/site/hero/not-happening-to-me.jpeg',
      alt: 'A woman in a black turtleneck looking directly at the viewer',
      position: 'object-right',
      // Her face sits ~81% across with her hair falling to the right. 93%
      // centres the compact window just right of her face so the hair stays in.
      compactPosition: 'object-[93%_50%]',
    },
    body: (
      <>
        I&rsquo;m very internet savvy &ndash; scams, that happens to folks that
        don&rsquo;t know how to spot them &ndash; that&rsquo;s not me!
      </>
    ),
  },
  {
    id: 'empowering-you',
    labelLines: ['Empowering', 'you'],
    headline: ['Informed', 'Aware', 'Empowered'],
    image: {
      src: '/images/site/hero/empowering-you.jpeg',
      alt: 'A woman working at a laptop in a book-lined room',
      position: 'object-right',
      // The compact panel shows about a third of the width, and the right
      // edge cuts her face in half. Her face sits ~69% across; 79% is the
      // position that puts the window's centre there.
      compactPosition: 'object-[79%_50%]',
    },
    body: (
      <>
        It&rsquo;s not about technology &ndash; it&rsquo;s about educating
        yourself and understanding your adversary. Use next-generation scammer
        prevention tools to avoid being an unsuspecting victim.
      </>
    ),
  },
  {
    id: 'know-the-enemy',
    labelLines: ['Know your', 'enemy'],
    headline: ['Predator', 'Lair', 'Abuser'],
    image: {
      src: '/images/site/hero/know-the-enemy.jpeg',
      alt: 'A figure in a suit and blank white mask offering a handshake',
      position: 'object-right',
      // The mask sits ~80% across. 92% centres the compact window on it and
      // still keeps the offered hand in frame at the left.
      compactPosition: 'object-[92%_50%]',
    },
    body: (
      <>
        Hi &ndash; hoping you got the emails and text I sent &ndash; we have so
        much in common &ndash; want to connect!
      </>
    ),
  },
];

/* ------------------------------------------------------------------ *
 * Pieces
 * ------------------------------------------------------------------ */

const SlideChip = ({ slide, isActive, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(slide.id)}
    aria-pressed={isActive}
    /* The label's lines are separate blocks, so spell the name out for
       assistive tech rather than leaving it to run the words together. */
    aria-label={slide.labelLines.join(' ')}
    /* Fixed width on a scrolling rail below md, equal columns above it —
       squeezing five chips into a phone width truncated every label. */
    className={`flex w-[150px] flex-shrink-0 snap-start flex-col justify-between rounded-[10px] border px-2 pb-2 pt-2 text-left transition-colors md:w-auto md:min-w-0 md:flex-1 xl:px-3 ${
      isActive
        ? 'border-white bg-white'
        : 'border-black/25 bg-[#D9D9D9] hover:bg-[#e6e6e6]'
    }`}
  >
    <img
      src="/images/site/nav/ai-cloud-black.png"
      alt=""
      className="h-5 w-auto self-start object-contain md:h-7"
    />
    {/* Labels break on their own fixed lines rather than wrapping to the box
        width, so the same words land on the same lines at every screen size.
        nowrap keeps a line whole even where the chip is at its narrowest. */}
    <span className="mt-2 flex items-end justify-between gap-1 text-[13px] leading-tight text-black xl:gap-2 xl:text-[15px]">
      <span>
        {slide.labelLines.map((line) => (
          <span key={line} className="block whitespace-nowrap">
            {line}
          </span>
        ))}
      </span>
      <span aria-hidden="true" className="flex-shrink-0">
        &gt;
      </span>
    </span>
  </button>
);

// "Innovation > Dynamic > Proactive." — the steps never break mid-word, and
// the closing full stop stays with the last one.
const Headline = ({ steps, className = '' }) => (
  <p className={`font-bold leading-tight text-white ${className}`}>
    {steps.map((step, index) => (
      <span key={step} className="whitespace-nowrap">
        {index > 0 && (
          <span aria-hidden="true" className="px-[0.35em]">
            &gt;
          </span>
        )}
        {step}
        {index === steps.length - 1 ? '.' : ''}{' '}
      </span>
    ))}
  </p>
);

// The mark beside "Download": an arrow dropping into the corner of a frame.
const DownloadMark = () => (
  <svg
    viewBox="0 0 28 28"
    className="h-7 w-7 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="1.5" y="1.5" width="25" height="25" />
    <path d="M6 6l7 7M13 8.5V13H8.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="15" y="15" width="9" height="9" fill="currentColor" stroke="none" />
  </svg>
);

const DownloadBlock = ({ to, className = '' }) => (
  <div className={className}>
    <Link
      to={to}
      className="inline-flex items-center gap-8 text-[18px] font-bold text-white hover:underline md:text-[20px]"
    >
      Download
      <DownloadMark />
    </Link>
    <p className="mt-4 text-base font-light text-white md:text-[19px]">
      Built like no other application.
    </p>
  </div>
);

const PlayVideoBar = ({ onPlay }) => (
  <div className="w-full bg-black">
    <div className="mx-auto flex w-full max-w-[1500px] items-center gap-6 px-4 py-6 md:gap-10 md:px-8">
      {/* Decorative only — the two buttons beside it are what open the film,
          so this is a mark rather than a control and stays out of the tab
          order and the accessibility tree. */}
      <div
        aria-hidden="true"
        className="flex h-[56px] w-[80px] flex-shrink-0 items-center justify-center border-2 border-white md:h-[72px] md:w-[105px]"
      >
        <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white md:h-9 md:w-9">
          <path d="M6 3.5v17l15-8.5z" />
        </svg>
      </div>
      <span aria-hidden="true" className="hidden h-px flex-shrink-0 bg-white sm:block sm:w-[120px] md:w-[200px]" />
      <div className="flex flex-wrap items-center gap-3 md:gap-5">
        <button
          type="button"
          onClick={onPlay}
          className="rounded-[6px] border border-white px-5 py-2 text-base text-white transition-colors hover:bg-white/10 md:px-7 md:py-3 md:text-lg"
        >
          No / SCAM
        </button>
        <button
          type="button"
          onClick={onPlay}
          className="rounded-[6px] border border-white px-5 py-2 text-base text-white transition-colors hover:bg-white/10 md:px-7 md:py-3 md:text-lg"
        >
          FBI Numbers
        </button>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ *
 * Hero
 * ------------------------------------------------------------------ */

const HeroSlideshow = ({ onPlayVideo }) => {
  const [activeId, setActiveId] = useState(SLIDES[0].id);
  const [paused, setPaused] = useState(false);
  const slide = SLIDES.find((item) => item.id === activeId) || SLIDES[0];
  const { image } = slide;
  const { authenticated } = useSubscription();
  const downloadTo = authenticated ? DOWNLOAD_PAGE : ROUTES.getStarted;

  // Advance on a timer. Listing activeId as a dependency restarts the clock
  // whenever a chip is picked, so a manual choice gets its full dwell time.
  useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const timer = setTimeout(() => {
      setActiveId((current) => {
        const index = SLIDES.findIndex((item) => item.id === current);
        return SLIDES[(index + 1) % SLIDES.length].id;
      });
    }, SLIDE_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [activeId, paused]);

  return (
    <>
      <section
        className="relative w-full overflow-hidden bg-black"
        aria-label="No / SCAM introduction"
        aria-roledescription="carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        {/* Layout per the 10-1 mockup, on solid black at every width. Desktop
            is two columns: the wordmark, the slide's copy and Download down
            the left; the slide's headline and its photograph on the right,
            each column closing its heading with a rule. Everything is held to
            90% of the container, the chip rail included, so the right edges
            line up. Below lg (phones, iPads in portrait) it is a single
            column — wordmark, headline, then the copy with a small portrait
            crop beside it, the photograph keeping to the right as on desktop. */}
        <div className="relative mx-auto flex w-full max-w-[1500px] flex-col px-4 pb-10 pt-5 md:px-8 lg:pt-8">
          <div className="grid grid-cols-1 lg:w-[90%] lg:grid-cols-[minmax(0,32%)_minmax(0,1fr)] lg:gap-x-[7%]">
            {/* ---- Headings ---- */}
            {/* mb-0 on the h1: App.css gives every heading an 8px bottom
                margin, which would set this rule lower than the headline's. */}
            <div className="border-b border-white pb-4 lg:flex lg:items-end lg:pb-7">
              <h1 className="!mb-0 text-[36px] font-bold leading-none text-white sm:text-[44px] lg:text-[34px] xl:text-[38px]">
                No / SCAM
              </h1>
            </div>
            {/* Two lines are reserved below sm, where the longest headline
                wraps, so the copy underneath doesn't shift between slides. */}
            <div className="flex min-h-[3.6rem] items-start pt-4 sm:min-h-0 lg:items-end lg:border-b lg:border-white lg:pb-7 lg:pt-0">
              <Headline
                steps={slide.headline}
                className="text-[22px] sm:text-[28px] lg:text-[30px] xl:text-[36px]"
              />
            </div>

            {/* ---- Copy ---- */}
            {/* Below lg the copy shares its row with the portrait crop; the
                row's min-height covers the longest copy at 320px, so the hero
                keeps one height as the slides rotate. At lg the crop is hidden
                and the column carries the copy with Download pinned to its
                foot, level with the foot of the photograph. */}
            <div className="mt-4 flex min-h-[176px] flex-col sm:min-h-[170px] md:min-h-[200px] lg:mt-7 lg:min-h-[250px]">
              {/* iPad band (md up to lg): the photograph takes 60% of the row
                  as a landscape panel instead of a narrow portrait crop.
                  Measured with Playwright at 768 / 810 / 820 / 834 / 1023:
                  the longest slide copy wraps to 164px at most in the 40%
                  that is left, inside the row's fixed 200px, so the hero
                  keeps one height on every slide. Past 60% the copy column
                  drops under ~250px and reads cramped. */}
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 md:grid-cols-[minmax(0,1fr)_60%] md:gap-6 lg:block">
                <p className="text-base font-light leading-snug text-white sm:text-lg lg:text-[17px] xl:text-[19px]">
                  {slide.body}
                </p>
                <img
                  src={image.src}
                  alt={image.alt}
                  /* md:object-right - at the wide iPad panel the crop anchors like the
                     desktop photograph (every slide's `position`), not like the portrait
                     crop the compactPosition values were tuned for. */
                  className={`h-[150px] w-[120px] object-cover sm:h-[170px] sm:w-[140px] md:h-[200px] md:w-full md:object-right lg:hidden ${
                    image.compactPosition || image.position || 'object-center'
                  }`}
                />
              </div>
              <DownloadBlock to={downloadTo} className="hidden lg:mt-auto lg:block lg:pt-6" />
            </div>

            {/* ---- Photograph (desktop) ---- */}
            {/* Fills the row rather than setting its height: the copy column's
                min-height decides how tall the row is, so every slide's
                photograph is the same size whatever its own proportions. */}
            <div className="relative hidden lg:mt-7 lg:block">
              <img
                key={slide.id}
                src={image.src}
                alt={image.alt}
                className={`absolute inset-0 h-full w-full object-cover ${image.position || 'object-center'}`}
              />
            </div>
          </div>

          <DownloadBlock to={downloadTo} className="mt-6 lg:hidden" />

          {/* Chip rail closes the hero. */}
          <div className="mt-8 w-full rounded-[20px] bg-white/[.66] p-3 md:p-4 lg:w-[90%]">
            <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto md:gap-4 md:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {SLIDES.map((item) => (
                <SlideChip
                  key={item.id}
                  slide={item}
                  isActive={item.id === activeId}
                  onSelect={setActiveId}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <PlayVideoBar onPlay={onPlayVideo} />
    </>
  );
};

export default HeroSlideshow;
